import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Building2, Map, Settings, LogOut, Share2, CreditCard, CalendarDays,
  Coins, Users, Key, Mail,
  ChevronDown, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { authGet, logout } from '../../utils/auth';
import useRequireAdmin from '../../hooks/useRequireAdmin';

// Dashboard card component
const DashboardCard = ({ icon: Icon, title, description, bullets, onClick, color = 'primary', testId }) => {
  const colorClasses = {
    primary: 'hover:border-primary group-hover:text-primary text-primary',
    green: 'hover:border-green-500 group-hover:text-green-500 text-green-600 dark:text-green-400',
    blue: 'hover:border-blue-500 group-hover:text-blue-500 text-blue-600 dark:text-blue-400',
    yellow: 'hover:border-yellow-500 group-hover:text-yellow-500 text-yellow-600 dark:text-yellow-400',
    indigo: 'hover:border-indigo-500 group-hover:text-indigo-500 text-indigo-600 dark:text-indigo-400',
    teal: 'hover:border-teal-500 group-hover:text-teal-500 text-teal-600 dark:text-teal-400',
    orange: 'hover:border-orange-500 group-hover:text-orange-500 text-orange-600 dark:text-orange-400',
    amber: 'hover:border-amber-500 group-hover:text-amber-500 text-amber-600 dark:text-amber-400',
  };

  const iconColor = colorClasses[color]?.split(' ').slice(-1)[0] || 'text-primary';
  const hoverBorder = colorClasses[color]?.split(' ')[0] || 'hover:border-primary';
  const hoverText = colorClasses[color]?.split(' ')[1] || 'group-hover:text-primary';

  return (
    <div
      onClick={onClick}
      className={`bg-card border border-border rounded-xl p-6 ${hoverBorder} hover:shadow-md transition-all cursor-pointer group`}
      data-testid={testId}
    >
      <Icon className={`w-10 h-10 ${iconColor} mb-3 group-hover:scale-110 transition-transform`} strokeWidth={1.5} />
      <h3 className={`text-xl font-heading font-semibold mb-2 ${hoverText} transition-colors`}>{title}</h3>
      <p className="text-sm text-muted-foreground mb-3">{description}</p>
      <div className="text-xs text-muted-foreground space-y-0.5">
        {bullets.map((bullet, i) => (
          <p key={i}>• {bullet}</p>
        ))}
      </div>
    </div>
  );
};

// Section component with optional collapse
const DashboardSection = ({ title, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="mb-8">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 mb-4 group"
      >
        {isOpen ? (
          <ChevronDown className="w-5 h-5 text-muted-foreground" />
        ) : (
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        )}
        <h2 className="text-lg font-heading font-semibold text-foreground group-hover:text-primary transition-colors">
          {title}
        </h2>
      </button>
      {isOpen && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {children}
        </div>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  // Server-verified admin gate (single source of truth). Non-admins / expired
  // sessions are redirected to /admin/login inside the hook.
  const { user: verifiedUser, checking } = useRequireAdmin();
  const [pendingCount, setPendingCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  // `location.state.user` is an optimistic placeholder for instant header
  // render only — it is never the gate; verifiedUser (from /auth/me) wins.
  const user = verifiedUser || location.state?.user || null;

  const fetchPendingCount = useCallback(async () => {
    try {
      // No lightweight count endpoint exists, so we reuse /admin/users and
      // count client-side. If a dedicated count endpoint is added later,
      // swap it in here to avoid downloading the full user list.
      const response = await authGet('/admin/users');
      const pending = response.data?.filter(u => u.role === 'pending' && u.access_requested)?.length || 0;
      setPendingCount(pending);
    } catch (error) {
      // Ignore error
    }
  }, []);

  useEffect(() => {
    if (verifiedUser) fetchPendingCount();
  }, [verifiedUser, fetchPendingCount]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login', { replace: true });
    toast.success('Logged out successfully');
  };
  
  if (checking) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-muted">
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-heading font-bold text-foreground" data-testid="admin-dashboard-heading">
                Admin Dashboard
              </h1>
              {user && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  {user.picture && (
                    <img src={user.picture} alt={user.name} className="w-6 h-6 rounded-full" />
                  )}
                  <span>{user.name}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/admin/users')}
                className="relative bg-card border border-border hover:bg-muted rounded-md px-4 py-2 font-medium transition-all inline-flex items-center gap-2 text-foreground"
                data-testid="user-management-btn"
              >
                <Users className="w-4 h-4" strokeWidth={1.5} />
                Users
                {pendingCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {pendingCount}
                  </span>
                )}
              </button>
              <button
                onClick={handleLogout}
                className="bg-red-600 text-white hover:bg-red-700 rounded-md px-4 py-2 font-medium transition-all inline-flex items-center gap-2"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" strokeWidth={1.5} />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Dashboard Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Content Management Section */}
        <DashboardSection title="Content Management">
          <DashboardCard
            icon={Building2}
            title="Business Management"
            description="Manage all businesses, approve submissions, and export data"
            bullets={['View and edit businesses', 'Approve/reject submissions', 'Export to CSV']}
            onClick={() => navigate('/admin/businesses')}
            color="primary"
            testId="business-management-card"
          />
          <DashboardCard
            icon={Map}
            title="BTCMap Management"
            description="Sync BTCMap data and manage settings"
            bullets={['Sync BTCMap.org data', 'View all BTCMap fields', 'Configure sync settings']}
            onClick={() => navigate('/admin/map-settings')}
            color="primary"
            testId="map-management-card"
          />
          <DashboardCard
            icon={CalendarDays}
            title="Events Management"
            description="Manage Bitcoin meetup groups and events"
            bullets={['Add meetup groups', 'Auto-scrape event data', 'Manual event creation']}
            onClick={() => navigate('/admin/events')}
            color="blue"
            testId="events-management-card"
          />
          <DashboardCard
            icon={Coins}
            title="Exchange Management"
            description="Manage Bitcoin-only exchanges"
            bullets={['Add/edit exchanges', 'Upload logos', 'Set display order']}
            onClick={() => navigate('/admin/exchanges')}
            color="yellow"
            testId="exchange-management-card"
          />
          <DashboardCard
            icon={CreditCard}
            title="POS Management"
            description="Manage point-of-sale services and features"
            bullets={['Add/edit POS services', 'Configure features', 'Comparison table']}
            onClick={() => navigate('/admin/pos')}
            color="green"
            testId="pos-management-card"
          />
          <DashboardCard
            icon={Share2}
            title="Social Media"
            description="Post to NOSTR and X via Make.com"
            bullets={['NOSTR & X connections', 'Post templates', 'Scheduled posting']}
            onClick={() => navigate('/admin/social-media')}
            color="indigo"
            testId="social-media-card"
          />
          <DashboardCard
            icon={Mail}
            title="Contact Submissions"
            description="Manage contact form messages"
            bullets={['View submissions', 'Track response status', 'API endpoints available']}
            onClick={() => navigate('/admin/contacts')}
            color="teal"
            testId="contacts-card"
          />
        </DashboardSection>

        {/* System Settings Section */}
        <DashboardSection title="System Settings">
          <DashboardCard
            icon={Settings}
            title="General Settings"
            description="Site configuration and preferences"
            bullets={['Regions & categories', 'Map tags', 'Country settings']}
            onClick={() => navigate('/admin/settings')}
            color="primary"
            testId="general-settings-card"
          />
          <DashboardCard
            icon={Key}
            title="API & Integration"
            description="Manage API keys and external integrations"
            bullets={['Generate API keys', 'Business CRUD endpoints', 'BTCMap routing flags']}
            onClick={() => navigate('/admin/api')}
            color="orange"
            testId="api-management-card"
          />
        </DashboardSection>


      </div>
    </div>
  );
};

export default AdminDashboard;
